/**
 * Indices of the points a line / area chart needs to look like the full series.
 *
 * Recharts renders an SVG node per point, and a John Deere combine logs every
 * second: one day is ~40k points, and the Tracks panel and the Weight Tracking
 * chart stalled on every pointer move. The chart has a few hundred pixels to
 * draw them in, so ~2k well-chosen points draw the same line.
 * Largest-Triangle-Three-Buckets (Steinarsson, 2013) keeps the peaks and dips
 * that a stride sample would skip.
 *
 * Display only: totals and weights are always computed on the full series.
 *
 * A null value is a line break (a data gap, or nothing measured). Each run of
 * values between nulls is downsampled on its own, with its share of the
 * budget, and the first and last null of every gap are kept — so the line
 * still breaks exactly where it did.
 */

/** Points a chart series is reduced to. */
export const CHART_MAX_POINTS = 2000;

export function downsampleIndices(
  xs: ArrayLike<number>,
  ys: ArrayLike<number | null | undefined>,
  target: number = CHART_MAX_POINTS
): number[] {
  const n = ys.length;
  if (n <= target || target < 3) return Array.from({ length: n }, (_, i) => i);

  // Runs of values and runs of nulls, in order.
  const runs: Array<{ start: number; end: number; gap: boolean }> = [];
  let values = 0;
  for (let i = 0; i < n; i++) {
    const gap = ys[i] == null;
    if (!gap) values++;
    const last = runs[runs.length - 1];
    if (last && last.gap === gap) last.end = i;
    else runs.push({ start: i, end: i, gap });
  }

  const out: number[] = [];
  for (const run of runs) {
    if (run.gap) {
      out.push(run.start);
      if (run.end !== run.start) out.push(run.end);
      continue;
    }
    const length = run.end - run.start + 1;
    const budget = Math.max(2, Math.round((target * length) / values));
    if (length <= budget) {
      for (let i = run.start; i <= run.end; i++) out.push(i);
    } else {
      lttbRun(xs, ys, run.start, run.end, budget, out);
    }
  }
  return out;
}

/** LTTB over ys[start..end] (no nulls inside), appending `budget` indices to `out`. */
function lttbRun(
  xs: ArrayLike<number>,
  ys: ArrayLike<number | null | undefined>,
  start: number,
  end: number,
  budget: number,
  out: number[]
): void {
  const y = (i: number) => ys[i] as number;
  out.push(start);
  if (budget > 2) {
    // The inner points, split into budget - 2 buckets; one point kept per bucket.
    const inner = end - start - 1;
    const size = inner / (budget - 2);
    let a = start;
    for (let b = 0; b < budget - 2; b++) {
      const lo = start + 1 + Math.floor(b * size);
      const hi = start + 1 + Math.floor((b + 1) * size);
      // The next bucket's average (the last point for the final bucket) is the
      // third corner of the triangle.
      const nextLo = hi;
      const nextHi = b === budget - 3 ? end + 1 : start + 1 + Math.floor((b + 2) * size);
      let cx = 0;
      let cy = 0;
      const count = Math.max(1, nextHi - nextLo);
      for (let j = nextLo; j < nextLo + count; j++) {
        cx += xs[j];
        cy += y(j);
      }
      cx /= count;
      cy /= count;

      let best = lo;
      let bestArea = -1;
      for (let j = lo; j < hi; j++) {
        const area = Math.abs((xs[a] - cx) * (y(j) - y(a)) - (xs[a] - xs[j]) * (cy - y(a)));
        if (area > bestArea) {
          bestArea = area;
          best = j;
        }
      }
      out.push(best);
      a = best;
    }
  }
  out.push(end);
}

/**
 * `rows` (in time order) cut to a zoomed window [from, to], plus one row beyond
 * each edge so the line still runs to the axis. A chart that zooms by narrowing
 * its axis domain downsamples this cut, not the whole day, or the detail never
 * comes back on zoom. Gap markers are rows with a time, so they are cut alike.
 */
export function sliceToWindow<T extends { time: number }>(rows: T[], from: number, to: number): T[] {
  let lo = 0;
  while (lo < rows.length && rows[lo].time < from) lo++;
  let hi = rows.length - 1;
  while (hi >= 0 && rows[hi].time > to) hi--;
  return rows.slice(Math.max(0, lo - 1), Math.min(rows.length, hi + 2));
}

/** `rows` reduced to what the chart needs, keyed on `key` (see downsampleIndices). */
export function downsampleRows<T extends { time: number }>(
  rows: T[],
  key: keyof T,
  target: number = CHART_MAX_POINTS
): { rows: T[]; indices: number[] } {
  if (rows.length <= target) {
    return { rows, indices: rows.map((_, i) => i) };
  }
  const indices = downsampleIndices(
    rows.map((r) => r.time),
    rows.map((r) => r[key] as unknown as number | null | undefined),
    target
  );
  return { rows: indices.map((i) => rows[i]), indices };
}
